/**
 * Converts HTML back to APCK markup.
 * Pure TypeScript - no DOM/jsdom dependency.
 */
const VOID_ELEMENTS = new Set([
    'area', 'base', 'br', 'col', 'embed', 'hr', 'img',
    'input', 'link', 'meta', 'source', 'track', 'wbr',
]);
function decodeEntities(s) {
    return s
        .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
        .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)))
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&amp;/g, '&');
}
function parseAttrs(s) {
    const attrs = {};
    const re = /([a-zA-Z_][\w\-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
    let m;
    while ((m = re.exec(s)) !== null) {
        attrs[m[1].toLowerCase()] = decodeEntities(m[2] ?? m[3] ?? m[4] ?? '');
    }
    return attrs;
}
/**
 * Parses an HTML string into a tree of nodes.
 * Handles the subset of HTML produced by the APCK renderer.
 */
function parseHtml(html) {
    const root = { type: 'element', tag: 'root', attrs: {}, children: [], parent: null };
    let current = root;
    let i = 0;
    while (i < html.length) {
        if (html[i] === '<') {
            // Skip comments
            if (html.startsWith('<!--', i)) {
                const end = html.indexOf('-->', i + 4);
                i = end === -1 ? html.length : end + 3;
                continue;
            }
            // Skip doctype / processing instructions
            if (html[i + 1] === '!' || html[i + 1] === '?') {
                const end = html.indexOf('>', i + 2);
                i = end === -1 ? html.length : end + 1;
                continue;
            }
            // Find end of tag, respecting quotes in attributes
            let j = i + 1;
            let inSingle = false;
            let inDouble = false;
            while (j < html.length) {
                const ch = html[j];
                if (ch === '"' && !inSingle)
                    inDouble = !inDouble;
                else if (ch === "'" && !inDouble)
                    inSingle = !inSingle;
                else if (ch === '>' && !inSingle && !inDouble)
                    break;
                j++;
            }
            if (j >= html.length) {
                // Malformed - treat rest as text
                current.children.push({ type: 'text', text: html.slice(i) });
                break;
            }
            const tagStr = html.slice(i + 1, j); // content between < and >
            i = j + 1;
            if (tagStr.startsWith('/')) {
                // Closing tag - walk up to find matching open element
                const tagName = tagStr.slice(1).trim().toLowerCase();
                let node = current;
                while (node && node.tag !== tagName) {
                    node = node.parent;
                }
                if (node && node.parent) {
                    current = node.parent;
                }
            }
            else {
                // Opening or self-closing tag
                const selfClosing = tagStr.endsWith('/');
                const content = selfClosing ? tagStr.slice(0, -1).trimEnd() : tagStr;
                const spaceIdx = content.search(/\s/);
                const tagName = (spaceIdx === -1 ? content : content.slice(0, spaceIdx)).toLowerCase();
                const attrStr = spaceIdx === -1 ? '' : content.slice(spaceIdx);
                const attrs = parseAttrs(attrStr);
                const el = { type: 'element', tag: tagName, attrs, children: [], parent: current };
                current.children.push(el);
                if (!selfClosing && !VOID_ELEMENTS.has(tagName)) {
                    current = el;
                }
            }
        }
        else {
            // Text node - collect until next <
            let j = i;
            while (j < html.length && html[j] !== '<')
                j++;
            const text = html.slice(i, j);
            if (text) {
                current.children.push({ type: 'text', text: decodeEntities(text) });
            }
            i = j;
        }
    }
    return root;
}
// Markup generation
export function htmlToMarkup(html) {
    if (!html)
        return '';
    const root = parseHtml(html);
    let markup = '[[markup]]\n\n';
    markup += processNodes(root.children);
    return markup.trim();
}
function processNodes(nodes) {
    let result = '';
    for (const node of nodes) {
        result += processNode(node);
    }
    return result;
}
function processNode(node) {
    if (node.type === 'text') {
        return node.text;
    }
    switch (node.tag) {
        case 'p':
            return processNodes(node.children).trim() + '\n\n';
        case 'br':
            return '\n';
        case 'h1':
            return `# ${processNodes(node.children)}\n\n`;
        case 'h2':
            return `## ${processNodes(node.children)}\n\n`;
        case 'h3':
            return `### ${processNodes(node.children)}\n\n`;
        case 'b':
            return `**${processNodes(node.children)}**`;
        case 'strong':
            return `**${processNodes(node.children)}**`;
        case 'i':
        case 'em':
            return `*${processNodes(node.children)}*`;
        case 'code':
            return `[[code:${processNodes(node.children)}]]`;
        case 'pre': {
            const codeContent = getTextContent(node);
            const indentedCode = codeContent
                .split('\n')
                .map((line) => '    ' + line)
                .join('\n');
            return `::\n\n${indentedCode}\n\n`;
        }
        case 'ul':
            return processList(node) + '\n';
        case 'a': {
            const href = node.attrs['href'] || '';
            const text = getTextContent(node);
            if (href === text) {
                return `[[${href}]]`;
            }
            return `[[${href}|${text}]]`;
        }
        case 'img': {
            const src = node.attrs['src'] || '';
            return `[[image:${src}]]`;
        }
        case 'table':
            return processTable(node) + '\n\n';
        default:
            return processNodes(node.children);
    }
}
function getTextContent(node) {
    if (node.type === 'text')
        return node.text;
    return node.children.map(getTextContent).join('');
}
function processList(ul) {
    let result = '';
    for (const child of ul.children) {
        if (child.type === 'element' && child.tag === 'li') {
            const content = processNodes(child.children).trim();
            const lines = content.split('\n');
            result += `- ${lines[0]}\n`;
            for (let i = 1; i < lines.length; i++) {
                if (lines[i].trim()) {
                    result += `  ${lines[i]}\n`;
                }
            }
        }
    }
    return result;
}
// Table helpers
function findElements(node, tag) {
    const results = [];
    for (const child of node.children) {
        if (child.type === 'element') {
            if (child.tag === tag)
                results.push(child);
            results.push(...findElements(child, tag));
        }
    }
    return results;
}
function directChildren(node, ...tags) {
    return node.children.filter((c) => c.type === 'element' && tags.includes(c.tag));
}
function findFirst(node, tag) {
    for (const child of node.children) {
        if (child.type === 'element') {
            if (child.tag === tag)
                return child;
            const found = findFirst(child, tag);
            if (found)
                return found;
        }
    }
    return null;
}
function processTable(table) {
    const rows = [];
    const isHeader = [];
    // Extract headers
    const thead = findFirst(table, 'thead');
    if (thead) {
        for (const tr of findElements(thead, 'tr')) {
            const row = [];
            for (const cell of directChildren(tr, 'th', 'td')) {
                row.push(processNodes(cell.children).trim());
            }
            if (row.length > 0) {
                rows.push(row);
                isHeader.push(true);
            }
        }
    }
    // Extract body
    const tbody = findFirst(table, 'tbody');
    const bodySource = tbody || table;
    for (const tr of directChildren(bodySource, 'tr')) {
        const row = [];
        for (const cell of directChildren(tr, 'td', 'th')) {
            row.push(processNodes(cell.children).trim());
        }
        if (row.length > 0) {
            rows.push(row);
            isHeader.push(false);
        }
    }
    if (rows.length === 0)
        return '';
    const numCols = Math.max(...rows.map((r) => r.length));
    if (numCols === 0)
        return '';
    // Group consecutive rows where subsequent rows have fewer cells than the first.
    // Only rows already column-spanning (fewer cells than numCols) can start groups.
    const groups = [];
    {
        let i = 0;
        while (i < rows.length) {
            const group = [i];
            if (rows[i].length < numCols) {
                while (i + 1 < rows.length && rows[i + 1].length < rows[group[0]].length) {
                    group.push(++i);
                }
            }
            groups.push(group);
            i++;
        }
    }
    // For each row, compute cell spans: [startCol, endCol] per cell.
    // When a row has fewer cells than numCols, the last cell spans remaining columns.
    const rowSpans = rows.map((row) => {
        const spans = [];
        for (let i = 0; i < row.length; i++) {
            if (i < row.length - 1) {
                spans.push([i, i]);
            }
            else {
                spans.push([i, numCols - 1]);
            }
        }
        return spans;
    });
    // Adjust rowSpans for sub-rows in multi-row groups: inherit r0's spans
    for (const group of groups) {
        if (group.length > 1) {
            const r0 = group[0];
            for (let k = 1; k < group.length; k++) {
                const ri = group[k];
                const nk = rows[ri].length;
                const newSpans = [];
                for (let ci = 0; ci < nk; ci++) {
                    newSpans.push(rowSpans[r0][ci]);
                }
                rowSpans[ri] = newSpans;
            }
        }
    }
    // Calculate column widths from non-spanning cells only
    const colWidths = Array(numCols).fill(0);
    rows.forEach((row, ri) => {
        row.forEach((cell, ci) => {
            const [start, end] = rowSpans[ri][ci];
            if (start === end) {
                const maxLen = cellMaxLineLen(cell);
                colWidths[start] = Math.max(colWidths[start], maxLen);
            }
        });
    });
    // Ensure spanning cells fit - grow last spanned column if needed
    rows.forEach((row, ri) => {
        row.forEach((cell, ci) => {
            const [start, end] = rowSpans[ri][ci];
            if (start < end) {
                const needed = cellMaxLineLen(cell);
                const available = spanContentWidth(colWidths, start, end);
                if (needed > available) {
                    colWidths[end] += needed - available;
                }
            }
        });
    });
    // Column boundary positions for a row (where '+' goes in separators)
    function getBoundaries(ri) {
        const b = new Set([0, numCols]);
        for (const [start, end] of rowSpans[ri]) {
            b.add(start);
            b.add(end + 1);
        }
        return b;
    }
    function makeSeparator(char, boundaries) {
        const sorted = [...boundaries].sort((a, b) => a - b);
        let sep = '+';
        for (let k = 0; k < sorted.length - 1; k++) {
            const from = sorted[k];
            const to = sorted[k + 1] - 1;
            sep += char.repeat(spanContentWidth(colWidths, from, to) + 2) + '+';
        }
        return sep;
    }
    // Separator between two adjacent rows uses the union of their boundaries
    function separatorBetween(above, below, char) {
        const boundaries = new Set([0, numCols]);
        if (above !== null)
            for (const b of getBoundaries(above))
                boundaries.add(b);
        if (below !== null)
            for (const b of getBoundaries(below))
                boundaries.add(b);
        return makeSeparator(char, boundaries);
    }
    // Build the ASCII table
    let result = '';
    let prevRepRow = null;
    for (const group of groups) {
        const r0 = group[0];
        if (group.length === 1) {
            // Single-row group: render as before
            const char = prevRepRow !== null && isHeader[prevRepRow] && !isHeader[r0] ? '=' : '-';
            result += separatorBetween(prevRepRow, r0, char) + '\n';
            const row = rows[r0];
            const spans = rowSpans[r0];
            const rowLines = Math.max(...row.map((cell) => cell.split('\n').length));
            for (let l = 0; l < rowLines; l++) {
                result += '|';
                for (let ci = 0; ci < row.length; ci++) {
                    const [start, end] = spans[ci];
                    const cellLines = row[ci].split('\n');
                    const content = cellLines[l] || '';
                    const width = spanContentWidth(colWidths, start, end);
                    result += ` ${content.padEnd(width)} |`;
                }
                result += '\n';
            }
            prevRepRow = r0;
        }
        else {
            // Multi-row group with row spanning
            const numR0Cells = rows[r0].length;
            const minCells = Math.min(...group.map((ri) => rows[ri].length));
            const r0Spans = rowSpans[r0];
            // Top separator
            const char = prevRepRow !== null && isHeader[prevRepRow] && !isHeader[r0] ? '=' : '-';
            result += separatorBetween(prevRepRow, r0, char) + '\n';
            // Split (ci < minCells) vs spanning (ci >= minCells) classification
            const isSplit = [];
            for (let ci = 0; ci < numR0Cells; ci++) {
                isSplit.push(ci < minCells);
            }
            // Spanning cells content (from r0)
            const spanContent = rows[r0].map((cell) => cell.split('\n'));
            // Sub-row heights (based on split cells only)
            const subRowHeights = group.map((ri) => {
                let h = 1;
                const nk = Math.min(rows[ri].length, minCells);
                for (let ci = 0; ci < nk; ci++) {
                    h = Math.max(h, rows[ri][ci].split('\n').length);
                }
                return h;
            });
            // Total visual lines = sum(sub-row heights) + separator lines between sub-rows
            let totalVisLines = subRowHeights.reduce((a, b) => a + b, 0) + (group.length - 1);
            // Grow last sub-row if spanning content exceeds total visual lines
            for (let ci = minCells; ci < numR0Cells; ci++) {
                if (spanContent[ci].length > totalVisLines) {
                    const deficit = spanContent[ci].length - totalVisLines;
                    subRowHeights[subRowHeights.length - 1] += deficit;
                    totalVisLines += deficit;
                }
            }
            // Pre-pad spanning cells' content to totalVisLines
            for (let ci = minCells; ci < numR0Cells; ci++) {
                while (spanContent[ci].length < totalVisLines) {
                    spanContent[ci].push('');
                }
            }
            // Walk visual lines sequentially
            let visLine = 0;
            for (let k = 0; k < group.length; k++) {
                const ri = group[k];
                const nk = rows[ri].length;
                // Split cell content for this sub-row
                const splitLines = [];
                for (let ci = 0; ci < minCells; ci++) {
                    const lines = (ci < nk ? rows[ri][ci] : '').split('\n');
                    while (lines.length < subRowHeights[k])
                        lines.push('');
                    splitLines.push(lines);
                }
                // Data lines for this sub-row
                for (let l = 0; l < subRowHeights[k]; l++) {
                    result += '|';
                    for (let ci = 0; ci < numR0Cells; ci++) {
                        const [start, end] = r0Spans[ci];
                        const width = spanContentWidth(colWidths, start, end);
                        const content = isSplit[ci]
                            ? splitLines[ci][l] || ''
                            : spanContent[ci][visLine] || '';
                        result += ` ${content.padEnd(width)} |`;
                    }
                    result += '\n';
                    visLine++;
                }
                // Partial separator between sub-rows
                if (k < group.length - 1) {
                    let sep = '';
                    for (let ci = 0; ci < numR0Cells; ci++) {
                        // Left / inter-cell boundary character
                        if (ci === 0) {
                            sep += isSplit[ci] ? '+' : '|';
                        }
                        else {
                            sep += isSplit[ci - 1] || isSplit[ci] ? '+' : '|';
                        }
                        const [start, end] = r0Spans[ci];
                        const width = spanContentWidth(colWidths, start, end);
                        if (isSplit[ci]) {
                            sep += '-'.repeat(width + 2);
                        }
                        else {
                            const content = spanContent[ci][visLine] || '';
                            sep += ` ${content.padEnd(width)} `;
                        }
                    }
                    // Right boundary
                    sep += isSplit[numR0Cells - 1] ? '+' : '|';
                    result += sep + '\n';
                    visLine++;
                }
            }
            prevRepRow = r0;
        }
    }
    result += separatorBetween(prevRepRow, null, '-');
    return result;
}
function cellMaxLineLen(cell) {
    const lines = cell.split('\n');
    return Math.max(...lines.map((l) => l.length));
}
/** Content width for a cell spanning columns [start..end] */
function spanContentWidth(colWidths, start, end) {
    let w = 0;
    for (let i = start; i <= end; i++)
        w += colWidths[i];
    // Each removed internal separator (` | `) adds 3 chars of content space
    w += 3 * (end - start);
    return w;
}
